import React, { useState } from 'react';
import './ServiceTimeline.css';
import { Popover, Typography, Box, Divider } from '@mui/material';
import Error from '@mui/icons-material/Error';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { ThemePalette } from 'mui.theme';
import { type AssetsOverview } from 'types/survivalAnalysis.types';

interface Service {
  date: Date;
  numberOfServices: number;
  services: AssetsOverview[];
}

interface TimelineItemProps {
  label: string;
  services: Service[];
  daysInMonth?: number;
  enableDarkTheme: boolean;
  onOpenInNewClick: (itemId: string) => void;
}

export const ServiceTimeline: React.FC<{
  services: Service[];
  enableDarkTheme: boolean;
  onOpenInNewClick: (itemId: string) => void;
}> = ({ services, enableDarkTheme, onOpenInNewClick }) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const servicesByMonth: Service[][] = Array.from({ length: 12 }, () => []);
  services.forEach((service) => {
    const month = service.date.getMonth();
    servicesByMonth[month].push(service);
  });

  const orderedMonths = [
    ...months.slice(currentMonth).map((month) => `${month} ${currentYear}`),
    ...months
      .slice(0, currentMonth)
      .map((month) => `${month} ${currentYear + 1}`),
  ];

  const orderedServicesByMonth = [
    ...servicesByMonth.slice(currentMonth),
    ...servicesByMonth.slice(0, currentMonth),
  ];

  return (
    <div
      className={
        enableDarkTheme ? 'timeline-container-dark' : 'timeline-container'
      }
    >
      {orderedMonths.map((month, index) => (
        <TimelineItem
          key={index}
          label={month}
          services={orderedServicesByMonth[index]}
          daysInMonth={new Date(
            currentYear,
            ((currentMonth + index) % 12) + 1,
            0
          ).getDate()}
          enableDarkTheme={enableDarkTheme}
          onOpenInNewClick={onOpenInNewClick}
        />
      ))}
    </div>
  );
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  label,
  services,
  enableDarkTheme = false,
  onOpenInNewClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverContent, setPopoverContent] = useState<AssetsOverview[]>([]);
  const [popoverDate, setPopoverDate] = useState<string>('');

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    services: AssetsOverview[],
    date: string
  ) => {
    // Filter high risk services, but ensure we are setting correct content for display
    const highRiskServices = services.filter(
      (service) => (service.high ?? 0) > 0
    );
    setAnchorEl(event.currentTarget);
    setPopoverContent(highRiskServices);
    setPopoverDate(date);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Calculate the count of high risk services for display in the timeline item circle
  const highRiskCount = services
    .flatMap((service) => service.services)
    .filter((s) => (s.high ?? 0) > 0).length;

  return (
    <div className="timeline-item">
      <div
        className={
          enableDarkTheme ? 'circle circle-dark' : 'circle circle-light'
        }
        onClick={(e) => {
          handlePopoverOpen(
            e,
            services.flatMap((service) => service.services),
            label
          );
        }}
      >
        <Typography variant="body2">{highRiskCount}</Typography>
      </div>
      <div className="label">
        <Typography variant="body2">{label}</Typography>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          style: {
            backgroundColor: enableDarkTheme
              ? ThemePalette.typography.black
              : ThemePalette.typography.white,
          },
        }}
      >
        <Box p={2}>
          <Typography
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.white
                : ThemePalette.typography.black,
            }}
            variant="body2"
          >
            High Risk Assets in {label}
          </Typography>
          <Divider
            sx={{
              marginY: 1,
              borderColor: enableDarkTheme
                ? ThemePalette.typography.white
                : ThemePalette.typography.black,
            }}
          />
          {popoverContent.map((service, index) => (
            <Box
              key={index}
              mb={2}
              sx={{
                marginTop: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                justifyContent: 'left',
              }}
            >
              <Typography
                variant="body2"
                style={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.black,
                }}
              >
                {service.eolDate ?? 'No EOL Date Available'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                }}
              >
                <Typography
                  style={{
                    color: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.black,
                  }}
                  variant="body1"
                >
                  {service.item_id ?? 'No Asset ID'}{' '}
                  {/* Ensure correct property is displayed */}
                </Typography>
                <OpenInNew
                  style={{ marginLeft: 5, color: 'grey', fontSize: '1rem' }}
                  onClick={() => {
                    onOpenInNewClick(service.asset_id ?? '');
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                  marginTop: 1,
                }}
              >
                <Error sx={{ color: 'red', fontSize: '1rem' }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ marginLeft: 0.5 }}
                  style={{
                    color: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.black,
                  }}
                >
                  {service.high ?? 0} {/* Display high risk count */}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </div>
  );
};
